/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import type {
  ExtensionPrompt,
  Metadata,
  MicrosoftPrompt,
  Prompt,
  PromptCategory,
  SearchPrompt,
  TenantInfo,
  TenantPrompt,
  UnifiedPrompt,
  UserExtensionPrompt,
  UserPrompt,
  WorkgroupMicrosoftPrompt,
  WorkgroupUserPrompt,
} from "@1js/pl-types";
import {
  AllowedExtensionList,
  AllowedJobTypeList,
  WXPProducts,
} from "@1js/pl-types";

/**
 * Check if the prompt is a BizChat prompt
 * @param prompt Prompt
 * @returns true if the prompt is a BizChat prompt
 */
export const isBizChatPrompt = (prompt: Prompt) => {
  return prompt.Products && prompt.Products?.indexOf("BizChat") > -1;
};

/**
 * Check if the prompt is a CWC prompt
 * @param prompt Prompt
 * @returns true if the prompt is a CWC prompt
 */
export const isCWCPrompt = (prompt: Prompt) => {
  return prompt.Products?.includes("CWC");
};

export const isWXPPrompt = (prompt: Prompt) => {
  return prompt.Products?.some((product) => WXPProducts.has(product));
};

/**
 * Check if the prompt is prompt that can be run in Copilot Chat
 * @param prompt Prompt
 * @returns true if the prompt is a Copilot Chat prompt
 */
export const isCopilotChatPrompt = (prompt: Prompt) => {
  return isBizChatPrompt(prompt) || isCWCPrompt(prompt);
};

/**
 * Check if the prompt is a User prompt
 * @param prompt Prompt
 * @returns true if the prompt is a User authored prompt
 */
export const isUserPrompt = (prompt: Prompt): prompt is UserPrompt => {
  return prompt.Origin === "User";
};

export const isTenantPrompt = (prompt: Prompt): prompt is TenantPrompt => {
  return prompt.Origin === "Tenant";
};

export const isNonSharedTenantPrompt = (
  prompt: Prompt
): prompt is TenantPrompt => {
  return isTenantPrompt(prompt) && !isSharedPrompt(prompt);
};

export const isGPTPrompt = (prompt: Prompt) => {
  return prompt.Origin === "GPT";
};

/**
 * Check if the prompt is an extension prompt
 * @param prompt Prompt
 * @returns true if the prompt is an extension prompt
 */
export const isExtensionPrompt = (
  prompt: Prompt
): prompt is UserExtensionPrompt | ExtensionPrompt => {
  return (
    (!!prompt && !!prompt.Origin && AllowedExtensionList.has(prompt.Origin)) ||
    (!!prompt && isUserCreatedExtensionPrompt(prompt))
  );
};

/**
 * Check if the prompt is a user created extension prompt
 * @param prompt Prompt
 * @returns true if the prompt is a user created extension prompt
 */
export const isUserCreatedExtensionPrompt = (
  prompt: Prompt
): prompt is UserExtensionPrompt => {
  return (
    !!prompt && isUserPrompt(prompt) && !!(prompt as UserExtensionPrompt).GptId
  );
};

export const isUnifiedPrompt = (prompt: Prompt | UnifiedPrompt) => {
  return "PromptId" in prompt;
};

/**
 * Check if the prompt is a workgroup prompt
 * @param prompt Prompt
 * @returns true if the prompt is a workgroup prompt
 */
export const isWorkgroupPrompt = (
  prompt: Prompt | undefined
): prompt is WorkgroupMicrosoftPrompt | WorkgroupUserPrompt => {
  return !!prompt?.Groups;
};

/**
 * Check if the prompt is a shared prompt (e.g. workgroup prompt or tenant shared prompt)
 * @param prompt Prompt
 */
export const isSharedPrompt = (prompt: Prompt) => {
  // Note: this method should be expanded in the future to include other types of shared prompts (e.g. tenant shared prompts)
  return isWorkgroupPrompt(prompt);
};

/**
 * Check if the prompt is a non-shared user prompt (i.e. user authored prompt that is not shared with a workgroup)
 * @param prompt Prompt
 */
export const isNonSharedUserPrompt = (prompt: Prompt): prompt is UserPrompt => {
  return isUserPrompt(prompt) && !isSharedPrompt(prompt);
};

/**
 * Check if the prompt is authored by Microsoft
 * @param prompt Prompt object
 * @returns true if the prompt is authored by Microsoft, false otherwise
 */
export const isMicrosoftPrompt = (
  prompt: Prompt
): prompt is MicrosoftPrompt => {
  return !isUserPrompt(prompt) && !isTenantPrompt(prompt);
};

export const isMicrosoftOrigin = (
  prompt: Prompt
): prompt is MicrosoftPrompt | WorkgroupMicrosoftPrompt => {
  return prompt.Origin === "Microsoft" || prompt.Origin === "Global";
};

/**
 * Check if the prompt is liked by the user
 * Note: This is different from isUserPrompt, which checks if the prompt is authored by the user
 * @param prompt Prompt object
 * @returns true if the prompt is liked by the user, false otherwise
 */
export const isLikedPrompt = (prompt: Prompt) => {
  // A prompt is liked if it is favorited and not authored by the user
  // This is because all user authored prompts are automatically favorited in the backend
  return (
    prompt.UserActivity?.Favorite?.IsFavorite && !isNonSharedUserPrompt(prompt)
  );
};

/**
 * Extract job type from the prompt
 * @param prompt Prompt
 * @returns job type list
 */
export const extractJobTypes = (prompt: Prompt): string[] => {
  // example meta data looks like this { name: "Industry", value: "Human Resources" }
  // go through all meta data from a prompt, and extract values of each meta data to a list only if the name is in the allowed list.
  const jobTypes: string[] = [];
  if (prompt && Array.isArray(prompt.Metadata)) {
    prompt.Metadata.forEach((item: Metadata) => {
      if (AllowedJobTypeList.has(item.Name)) {
        jobTypes.push(item.Value);
      }
    });
  }

  return jobTypes;
};

export const transformToTenantPrompts = (
  prompts: UnifiedPrompt[]
): TenantPrompt[] => {
  return prompts.map((item: UnifiedPrompt) => ({
    CommandText: item.CommandText,
    DisplayText: item.DisplayText,
    Title: item.Title ?? "",
    UserActivity: item.UserActivity ?? {
      Favorite: {
        IsFavorite: false,
        ActivityDateTimeInUtc: "",
      },
    },
    Id: `${item.PromptStoreId}`,
    PromptId: item.PromptStoreId,
    DisplayCategory:
      (item.ProfileAttributes?.TaskTypes?.[0] as PromptCategory) ?? "",
    Products: item.Products ?? [],
    Origin: "Tenant",
    Publisher: {
      Name: item.ProviderDetails?.ProviderName ?? "",
    },
    ProviderName: item.ProviderDetails?.ProviderName,
    Metadata: [
      ...(item.ProfileAttributes?.Roles?.[0]
        ? [
            {
              Name: "Role",
              Value: item.ProfileAttributes.Roles[0],
            },
          ]
        : []),
      ...(item.ProfileAttributes?.Departments?.[0]
        ? [
            {
              Name: "Department",
              Value: item.ProfileAttributes.Departments[0],
            },
          ]
        : []),
    ],
  }));
};

/**
 * Transform search prompts to web prompts
 * @param searchPrompts SearchPrompt[]
 * @param user User object
 * @param host Host string
 * @returns Prompt[]
 */
export const transformSearchPromptsToWebPrompts = (
  searchPrompts: SearchPrompt[],
  user: any, //TODO: marked as any for now, need to be replaced with the correct type which is right now in pl-web-components
  host: string
): Prompt[] => {
  // First map search attributes to prompt attributes
  let prompts: Prompt[] = searchPrompts.map((searchPrompt) => {
    const {
      SharedDetails,
      ProviderDetails,
      AgentDetails,
      ProviderTypes,
      Origin,
      ...promptbase
    } = searchPrompt;

    return {
      ...promptbase,
      ParentPromptId: SharedDetails?.[0]?.ParentPromptId,
      Publisher: {
        Address: ProviderDetails?.[0]?.AuthorEmailAddress,
        Name: ProviderDetails?.[0]?.AuthorName,
      },
      Workgroup: {
        Id: SharedDetails?.[0]?.GroupId,
      },
      ProviderId: AgentDetails?.[0]?.Id,
      ProviderName: AgentDetails?.[0]?.Name,
      ProviderPartyType: AgentDetails?.[0]?.Type,
      DeveloperName: AgentDetails?.[0]?.DeveloperName,
      SourcePromptId: ProviderDetails?.[0]?.PromptId,
      Origin:
        !Origin &&
        ProviderTypes?.some(
          (provider) =>
            provider === "SavedPrompts" ||
            provider === "WorkgroupPublishPrompts"
        )
          ? "User"
          : Origin,
    } as Prompt;
  });

  //TODO: The transform below was copied from saved prompt and team prompt api calls. We should rewrite calls to utilize this
  // Add user information to the prompts
  prompts = prompts.map((prompt) => {
    if (
      prompt.Origin == "User" &&
      !("ParentPromptId" in prompt && prompt.ParentPromptId)
    ) {
      return {
        ...prompt,
        DisplayCategory: "Created by Me",
        Author: {
          DisplayName: user?.displayName ?? "",
          Email: user?.email ?? "",
          Avatar: user
            ? `https://${host}/api/graph/users/${user.objectId}/photos/48x48/$value`
            : undefined,
        },
      };
    }
    return prompt;
  });

  // Add team prompts information to the prompts
  prompts = prompts.map((prompt: Prompt) => {
    if ("ParentPromptId" in prompt && prompt.ParentPromptId) {
      return {
        ...prompt,
        Groups: prompt.Workgroup
          ? [{ ...prompt.Workgroup, IsMember: true, HasPrompts: true }]
          : [],
        Author:
          prompt.Origin === "User" && prompt.Publisher
            ? {
                DisplayName: prompt.Publisher.Name,
                Email: prompt.Publisher.Address,
                Avatar: prompt.Publisher.Address
                  ? `https://${host}/api/graph/users/${prompt.Publisher.Address}/photos/48x48/$value`
                  : undefined,
              }
            : undefined,
      };
    }
    return prompt;
  });

  return prompts;
};

export const patchTenantInfoToPrompts = (
  prompt: Prompt,
  tenantInfo?: TenantInfo
): void => {
  if (!prompt || !tenantInfo || !isTenantPrompt(prompt)) {
    return;
  }

  if (tenantInfo.displayName) {
    prompt.ProviderName = tenantInfo.displayName;
  }
  if (tenantInfo.logo) {
    prompt.Metadata = [
      ...(prompt.Metadata || []),
      {
        Name: "OrganizationLogo",
        Value: tenantInfo.logo,
      },
    ];
  }
};

export const patchTenantInfoAndProfieAttributesToPrompts = (
  prompt: Prompt,
  tenantInfo?: TenantInfo
): void => {
  if (!prompt) {
    return;
  }

  if (prompt.ProfileAttributes) {
    prompt.Metadata = [
      ...(prompt.ProfileAttributes?.Roles?.[0]
        ? [
            {
              Name: "Role",
              Value: prompt.ProfileAttributes.Roles[0],
            },
          ]
        : []),
      ...(prompt.ProfileAttributes?.Departments?.[0]
        ? [
            {
              Name: "Department",
              Value: prompt.ProfileAttributes.Departments[0],
            },
          ]
        : []),
    ];
  }

  if (!tenantInfo) {
    return;
  }

  if (tenantInfo.displayName) {
    prompt.ProviderName = tenantInfo.displayName;
  }
  if (tenantInfo.logo) {
    prompt.Metadata = [
      ...(prompt.Metadata || []),
      {
        Name: "OrganizationLogo",
        Value: tenantInfo.logo,
      },
    ];
  }
};
